import React, { useState } from 'react';
import { FiMusic, FiMessageCircle, FiVideo, FiCheckCircle, FiRefreshCw, FiCpu, FiInfo, FiHeart, FiAlertTriangle } from 'react-icons/fi';
import tiktokService from '../services/tiktokService';
import './CommentsPage.css';

import Loading from './Loading';
const TikTokComments = ({ tiktokUserId, accountName, accounts, onAccountChange }) => {
  const [loading, setLoading] = useState(false);
  const [comments, setComments] = useState([]);
  const [sentimentAnalysis, setSentimentAnalysis] = useState(null);
  const [error, setError] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [stats, setStats] = useState(null);
  const [expandedVideos, setExpandedVideos] = useState({});

  const fetchComments = async (analyzeEnabled = false) => {
    setLoading(true);
    setError(null);
    setWarnings([]);

    try {
      const response = await tiktokService.getComments(
        tiktokUserId,
        10,  // limit_videos
        50,  // comments_per_video
        analyzeEnabled
      );

      setStats({
        total_comments: response.data.total_comments,
        videos_analyzed: response.data.videos_analyzed,
        comments_saved: response.data.comments_saved
      });

      setComments(response.data.comments_by_video || {});
      setWarnings(response.data.warnings || []);

      if (response.data.sentiment_analysis) {
        setSentimentAnalysis(response.data.sentiment_analysis);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Error al obtener comentarios');
      console.error('Error fetching TikTok comments:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !stats) {
    return (
      <div className="comments-page">
        <Loading fullScreen={false} size="sm" mensaje="Cargando comentarios de TikTok" />
      </div>
    );
  }

  return (
    <div className="comments-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <div className="header-left">
            <div className="header-icon">
              <FiMusic size={32} />
            </div>
            <div>
              <h1>Comentarios de TikTok</h1>
              <p className="subtitle">{accountName}</p>
            </div>
          </div>

          <div className="header-actions">
            {/* Account Selector */}
            {accounts && accounts.length > 1 && (
              <select
                value={tiktokUserId || ''}
                onChange={(e) => {
                  const account = accounts.find(a => a.tiktok_user_id === e.target.value);
                  if (account && onAccountChange) {
                    onAccountChange(account);
                  }
                }}
                style={{
                  padding: '0.75rem 1rem',
                  borderRadius: '10px',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  background: 'white',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  color: '#102826',
                  cursor: 'pointer',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.2s',
                  minWidth: '180px'
                }}
                onMouseOver={(e) => {
                  e.target.style.borderColor = '#10b981';
                  e.target.style.boxShadow = '0 8px 12px -2px rgba(0, 0, 0, 0.15)';
                }}
                onMouseOut={(e) => {
                  e.target.style.borderColor = 'rgba(0, 0, 0, 0.1)';
                  e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                }}
              >
                {accounts.map((account) => (
                  <option key={account.tiktok_user_id} value={account.tiktok_user_id}>
                    @{account.username}
                  </option>
                ))}
              </select>
            )}

            <button
              onClick={() => fetchComments(false)}
              disabled={loading}
              className="btn btn-primary"
            >
              <FiRefreshCw className={loading ? 'spinning' : ''} />
              {loading ? 'Cargando...' : 'Obtener Comentarios'}
            </button>

            <button
              onClick={() => fetchComments(true)}
              disabled={loading}
              className="btn btn-primary"
            >
              <FiCpu className={loading ? 'spinning' : ''} />
              {loading ? 'Analizando...' : 'Obtener y Analizar IA'}
            </button>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="alert-error">
          <div className="alert-error-content">
            <FiInfo size={20} className="alert-error-icon" />
            <p className="alert-error-text">{error}</p>
          </div>
        </div>
      )}

      {/* Warnings Alert */}
      {warnings.length > 0 && (
        <div className="alert-error" style={{
          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
          borderLeftColor: '#f59e0b',
          borderColor: 'rgba(245, 158, 11, 0.2)'
        }}>
          <div className="alert-error-content">
            <FiAlertTriangle size={20} style={{ color: '#f59e0b' }} />
            <div>
              <p style={{ color: '#92400e', fontWeight: 600, marginBottom: '0.5rem' }}>Advertencias:</p>
              <ul style={{ margin: 0, paddingLeft: '1.5rem', color: '#92400e' }}>
                {warnings.map((warning, idx) => (
                  <li key={idx} style={{ marginBottom: '0.25rem' }}>{warning}</li>
                ))}
              </ul>
              <p style={{
                color: '#78350f',
                fontSize: '0.85rem',
                marginTop: '0.75rem',
                fontStyle: 'italic',
                background: 'rgba(255, 255, 255, 0.5)',
                padding: '0.5rem',
                borderRadius: '6px'
              }}>
                <strong>Nota:</strong> La API de TikTok requiere permisos especiales para acceder a comentarios.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <FiMessageCircle />
            </div>
            <div className="stat-content">
              <div className="stat-label">Total Comentarios</div>
              <div className="stat-value">{stats.total_comments}</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <FiVideo />
            </div>
            <div className="stat-content">
              <div className="stat-label">Videos Analizados</div>
              <div className="stat-value">{stats.videos_analyzed}</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <FiCheckCircle />
            </div>
            <div className="stat-content">
              <div className="stat-label">Comentarios Guardados</div>
              <div className="stat-value">{stats.comments_saved}</div>
            </div>
          </div>
        </div>
      )}

      {/* Sentiment Analysis */}
      {sentimentAnalysis && (
        <div className="sentiment-section">
          <div className="sentiment-header">
            <FiCpu size={24} style={{ color: '#10b981' }} />
            <h3>Análisis de Sentimientos con IA</h3>
          </div>

          <div className="sentiment-grid">
            <div className="sentiment-card">
              <div className="sentiment-emoji">😊</div>
              <div className="sentiment-label">Positivos</div>
              <div className="sentiment-value positive">
                {sentimentAnalysis.sentiment_distribution?.positive || 0}
              </div>
            </div>

            <div className="sentiment-card neutral">
              <div className="sentiment-emoji">😐</div>
              <div className="sentiment-label">Neutrales</div>
              <div className="sentiment-value neutral">
                {sentimentAnalysis.sentiment_distribution?.neutral || 0}
              </div>
            </div>

            <div className="sentiment-card negative">
              <div className="sentiment-emoji">😞</div>
              <div className="sentiment-label">Negativos</div>
              <div className="sentiment-value negative">
                {sentimentAnalysis.sentiment_distribution?.negative || 0}
              </div>
            </div>
          </div>

          {sentimentAnalysis.insights && (
            <div className="insights-box">
              <div className="insights-header">
                <FiInfo size={18} />
                <h4>Insights de OpenAI:</h4>
              </div>
              <p className="insights-text">{sentimentAnalysis.insights}</p>
            </div>
          )}
        </div>
      )}

      {/* Comments by Video */}
      {Object.keys(comments).length > 0 && (
        <div className="comments-section">
          <h3 className="section-title">
            <FiMessageCircle size={28} style={{ color: '#10b981' }} />
            Comentarios por Video
          </h3>

          {Object.entries(comments)
            .filter(([videoId, videoData]) => videoData.comments && videoData.comments.length > 0)
            .map(([videoId, videoData]) => {
              const isExpanded = expandedVideos[videoId];
              const toggleExpand = () => {
                setExpandedVideos(prev => ({
                  ...prev,
                  [videoId]: !prev[videoId]
                }));
              };

              return (
                <div key={videoId} className="post-card">
                  <div className="post-header">
                    <div className="post-info">
                      <h4>Video de TikTok</h4>
                      {videoData.video_info.title && (
                        <p className="post-caption">{videoData.video_info.title}</p>
                      )}
                    </div>
                    {videoData.video_info.share_url && (
                      <a
                        href={videoData.video_info.share_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="post-link"
                      >
                        <FiMusic size={16} />
                        Ver en TikTok
                      </a>
                    )}
                  </div>

                  <button
                    onClick={toggleExpand}
                    className="toggle-comments-btn"
                  >
                    <FiMessageCircle size={18} />
                    <span>
                      {isExpanded ? 'Ocultar' : 'Ver'} {videoData.comments.length} comentario{videoData.comments.length !== 1 ? 's' : ''}
                    </span>
                    <span className="toggle-icon">{isExpanded ? '▲' : '▼'}</span>
                  </button>

                  {isExpanded && (
                    <div className="comments-list">
                      {videoData.comments.map((comment, idx) => (
                        <div key={idx} className="comment-item">
                          <div className="comment-content">
                            <div className="comment-text-wrapper">
                              <span className="comment-username">
                                {comment.user?.display_name || '👤 Usuario'}
                              </span>
                              <p className="comment-text">{comment.text}</p>
                            </div>
                            <div className="comment-likes">
                              <FiHeart size={16} />
                              {comment.like_count}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}

      {/* Empty State */}
      {Object.keys(comments).length === 0 && !loading && !error && (
        <div className="empty-state">
          <FiMessageCircle size={48} />
          <p>No hay comentarios disponibles</p>
          <p style={{ fontSize: '0.9rem', color: '#6b7280', marginTop: '0.5rem' }}>
            Haz clic en "Obtener Comentarios" para cargar los comentarios de tus videos.
          </p>
        </div>
      )}
    </div>
  );
};

export default TikTokComments;
